import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { BarChart3, LifeBuoy, ShieldCheck, TrendingUp, Activity, Repeat, AlertTriangle, Info } from "lucide-react";
import { DashboardShell, PageHead, EmptyState, ErrorState } from "@/components/DashboardShell";
import { journeyApi } from "@/lib/journeyApi";

// DISPLAY labels only. Underlying event keys / DB values / API identifiers are
// unchanged (money_landed_opened, sprint_confirmed remain the stored keys).
const EVENT_LABELS = {
  page_view: "Page views",
  money_landed_opened: "Edit started",
  sprint_confirmed: "Edits confirmed",
  protection_confirmed: "Protection confirmed",
  goal_set: "Goals set",
  goal_completed: "Goals completed",
  expected_income_added: "Expected income added",
  expected_income_received: "Expected income received",
  mortgage_scenario_viewed: "Mortgage scenario viewed",
  freedom_scenario_viewed: "Freedom scenario viewed",
  help_opened: "Help opened",
  support_submitted: "Support submitted",
  export_downloaded: "Exports downloaded",
  foundation_income_completed: "Foundation · Income completed",
  foundation_essentials_completed: "Foundation · Essentials completed",
  foundation_living_completed: "Foundation · Living Bucket completed",
  foundation_protection_completed: "Foundation · Protection completed",
  foundation_debts_completed: "Foundation · Debts completed",
};

const pct = (v) => (v == null ? "—" : `${v}%`);
const ofUsers = (n, d, noun = "eligible users") =>
  d == null || d === 0 ? `${n ?? 0} of ${d ?? 0} ${noun}` : `${n} of ${d} ${noun}`;

function Metric({ label, value, sub, hero, testid, definition }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-xl border p-4 ${hero ? "border-[#C9A86A] bg-[#FBF7EF]" : "border-[#E5E0D9] bg-white"}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-1">
        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A89A88]">{label}</p>
        {definition && (
          <button
            type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
            aria-label={`Show definition of ${label}`}
            data-testid={testid ? `${testid}-def` : undefined}
            className="shrink-0 rounded text-[#B5A892] hover:text-[#164C3B] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2F6B57]">
            <Info className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <p className={`mt-1 font-serif tabular-nums ${hero ? "text-3xl text-[#164C3B]" : "text-2xl text-[#171717]"}`}>{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-[#8A7C6B]">{sub}</p>}
      {definition && open && (
        <div className="mt-2 rounded-lg border border-[#E5E0D9] bg-[#FBF7EF] p-2.5 text-[11px] leading-relaxed text-[#70584A]" data-testid={testid ? `${testid}-def-panel` : undefined}>
          {definition}
        </div>
      )}
    </div>
  );
}

function Trend({ t, noun = "now" }) {
  if (!t) return null;
  return <span className="text-[11px] text-[#8A7C6B]">{t.current} {noun} · {t.prior} prior</span>;
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="mt-5 rounded-2xl border border-[#E5E0D9] bg-white p-5" data-testid={testid}>
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#164C3B]"><Icon className="h-4 w-4" /> {title}</p>
      <div className="mt-3">{children}</div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [includeTest, setIncludeTest] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [ov, setOv] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | error

  useEffect(() => { journeyApi.myRole().then((r) => setIsOwner(!!r.is_owner)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setState("loading");
    try {
      const a = await journeyApi.isAdmin();
      if (!a.is_admin) { setForbidden(true); setState("ready"); return; }
      const overview = await journeyApi.analyticsOverview(days, includeTest);
      setOv(overview); setState("ready");
    } catch { setState("error"); }
  }, [days, includeTest]);

  useEffect(() => { load(); }, [load]);

  if (state === "loading") return <DashboardShell><PageHead title="Admin intelligence" backToHome /><div className="h-40 animate-pulse rounded-2xl bg-[#F0EBE2]" /></DashboardShell>;
  if (state === "error") return <DashboardShell><PageHead title="Admin intelligence" backToHome /><ErrorState onRetry={load} testid="analytics-error" /></DashboardShell>;
  if (forbidden) return <DashboardShell><PageHead title="Admin intelligence" backToHome /><EmptyState icon={BarChart3} title="Not available" body="This internal console is only available to administrators." testid="analytics-forbidden" /></DashboardShell>;

  const o = ov?.overview || {};
  const funnel = ov?.funnel || [];
  const ret = ov?.retention || {};
  const cap = ov?.capability_adoption || {};
  const attn = ov?.attention || {};
  const trends = ov?.trends || {};
  const product = ov?.product_intelligence || [];
  const messages = attn.messages || [];
  const funnelMax = Math.max(1, ...funnel.map((f) => f.users));
  const rangeLabel = days === 365 ? "all time" : `last ${days} days`;

  const medianLabel = ret.repeat_all_same_day
    ? "Same day"
    : (ret.median_days_between_distinct_day_edits == null
        ? "—"
        : `${ret.median_days_between_distinct_day_edits} days`);

  return (
    <DashboardShell>
      <PageHead title="Admin intelligence" sub={`Privacy-safe product signals, ${rangeLabel}. No customer financial values are shown.`} />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Link to="/dashboard/admin/support" className="inline-flex items-center gap-2 rounded-xl border border-[#E5E0D9] bg-white px-3.5 py-2 text-sm font-medium text-[#164C3B] hover:bg-[#F0EBE2]" data-testid="admin-support-link">
          <LifeBuoy className="h-4 w-4" /> Support console
        </Link>
        {isOwner && (
          <Link to="/dashboard/admin/access" className="inline-flex items-center gap-2 rounded-xl border border-[#E5E0D9] bg-white px-3.5 py-2 text-sm font-medium text-[#164C3B] hover:bg-[#F0EBE2]" data-testid="admin-access-link">
            <ShieldCheck className="h-4 w-4" /> Admin access
          </Link>
        )}
        <div className="ml-auto flex items-center gap-2">
          <label htmlFor="analytics-range" className="sr-only">Time range</label>
          <select id="analytics-range" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="analytics-range"
            className="rounded-lg border border-[#DDE7DF] bg-white px-3 py-1.5 text-sm text-[#171717] focus:border-[#2F6B57] focus:outline-none">
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={365}>All time</option>
          </select>
          <label className="flex items-center gap-1.5 text-xs text-[#70584A]" data-testid="analytics-test-toggle">
            <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} /> Include test accounts
          </label>
        </div>
      </div>

      {/* EARLY DATA CONTEXT (quiet, not an alert) */}
      <div className="rounded-xl border border-[#E5E0D9] bg-[#FBF9F4] px-4 py-2.5 text-[11px] leading-relaxed text-[#8A7C6B]" data-testid="early-data-note">
        <span className="font-semibold uppercase tracking-[0.12em] text-[#A89A88]">Early data</span>
        {" · "}Percentages may move significantly as more customers use the product. Rates show their counts ({includeTest ? "test accounts included" : "known test accounts excluded"}).
      </div>

      {/* ATTENTION NEEDED — factual statements only */}
      {messages.length > 0 && (
        <Section icon={AlertTriangle} title="What needs attention" testid="admin-attention-needed">
          <ul className="space-y-2">
            {messages.map((m, i) => (
              <li key={i} className="flex gap-2 text-sm leading-relaxed text-[#525252]" data-testid={`attention-msg-${i}`}>
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#C9A86A]" />
                <span>{m}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11px] text-[#A89A88]">These are factual counts. They describe what to review, not why it happened.</p>
        </Section>
      )}

      {/* OVERVIEW */}
      <Section icon={BarChart3} title="Overview" testid="admin-overview">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <Metric label="Total users" value={o.total_users ?? 0} sub={includeTest ? "test accounts included" : "test accounts excluded"} testid="metric-total-users" />
          <Metric label="Active (window)" value={o.active_window ?? 0} sub={<Trend t={trends.active_users} />}
            definition="Unique users with at least one meaningful (non page-view) action in the selected period." testid="metric-active" />
          <Metric label="First Edit rate" value={pct(o.first_edit_rate)} sub={ofUsers(o.first_edit_numerator, o.first_edit_denominator, "onboarded users")}
            definition="Numerator: onboarded users with at least one confirmed Edit. Denominator: users who completed onboarding (the exact backend cohort). Confirmed Edits only — drafts, proposals, corrections and unassigned adjustments do not count." testid="metric-first-edit" />
          <Metric label="Second Edit rate" value={pct(o.second_edit_rate)} sub={ofUsers(o.second_edit_numerator, o.second_edit_denominator, "activated users")} hero
            definition="Numerator: activated users with at least two distinct confirmed Edits. Denominator: activated users (at least one confirmed Edit). Repeat Edits on the same day still count here — see Retention for later-day return." testid="metric-second-edit" />
          <Metric label="Open support" value={o.open_support ?? 0} testid="metric-open-support" />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Metric label="New users" value={o.new_window ?? 0} sub={<Trend t={trends.new_users} />}
            definition="Accounts created within the selected time range." />
          <Metric label="Returning users" value={o.returning_users ?? 0} sub="active on 2+ distinct days"
            definition="Users with meaningful activity on at least two different calendar days within the selected window." />
          <Metric label="Onboarding completion" value={pct(o.onboarding_rate)} sub={ofUsers(o.onboarding_numerator, o.onboarding_denominator, "accounts")}
            definition="Numerator: users who completed onboarding (have a saved foundation). Denominator: accounts created in the selected eligible cohort." />
          <Metric label="Active entitlements" value={o.active_entitlements ?? 0} sub="paid access, canonical source"
            definition="Active entitlement records keyed by the canonical user_id (not legacy uid). Test/complimentary accounts excluded unless 'Include test accounts' is on. Owner/admin accounts legitimately have none — admin access is independent of purchase." testid="metric-entitlements" />
        </div>
      </Section>

      {/* JOURNEY */}
      <Section icon={TrendingUp} title="First Edit journey (unique users)" testid="admin-journey">
        <div className="space-y-2">
          {funnel.map((f, i) => {
            const prior = i > 0 ? funnel[i - 1].users : f.users;
            const conv = prior ? Math.round((f.users / prior) * 100) : null;
            return (
              <div key={f.stage} data-testid={`funnel-${i}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#171717]">{f.stage}</span>
                  <span className="tabular-nums text-[#70584A]">{f.users} {i > 0 && conv != null && <span className="text-[#A89A88]">· {conv}% of {prior} prior</span>}</span>
                </div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-[#F0EBE2]">
                  <div className="h-full rounded-full bg-[#2F6B57]" style={{ width: `${Math.max(3, (f.users / funnelMax) * 100)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-[11px] text-[#A89A88]">Unique users per stage — not raw event counts. Each percentage is relative to the prior stage.</p>
      </Section>

      {/* RETENTION — repeat activity vs true later-day return */}
      <Section icon={Repeat} title="Retention" testid="admin-retention">
        <p className="mb-3 text-[11px] leading-relaxed text-[#8A7C6B]">Repeat Edits are not automatically retention. A second Edit on the same day is repeat activity; a return on a later calendar day is a stronger signal.</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <Metric label="Second Edit rate" value={pct(ret.second_edit_rate)} sub={ofUsers(ret.second_edit_users, ret.second_edit_denominator, "activated users")} hero
            definition="Activated users with at least two distinct confirmed Edits, over activated users." />
          <Metric label="Second Edit · later day" value={ret.second_edit_later_day ?? 0} sub={`${ret.second_edit_same_day ?? 0} same-day`}
            definition="Of users with two or more confirmed Edits, how many had a second Edit on a later calendar day (vs the same day)." testid="metric-later-day" />
          <Metric label="Returned on a later day" value={ret.returned_later_day ?? 0} sub="2+ distinct active days"
            definition="Users with meaningful activity on at least two different calendar days in the window." />
          <Metric label="Returned within 7 days" value={ret.returned_within_7d ?? 0}
            definition="Users whose first later active day fell within 7 days of their first active day in the window." />
          <Metric label="Returned within 30 days" value={ret.returned_within_30d ?? 0}
            definition="Users whose first later active day fell within 30 days of their first active day in the window." />
          <Metric label="Third+ Edit rate" value={pct(ret.third_plus_edit_rate)} sub={ofUsers(ret.third_plus_edit_users, ret.third_plus_edit_denominator, "activated users")}
            definition="Activated users with at least three confirmed Edits, over activated users." />
          <Metric label="Median days / distinct-day Edit" value={medianLabel}
            definition="Median gap in days between confirmed Edits on distinct calendar days. 'Same day' means every repeat Edit occurred on one day, so there is no later-day gap yet." testid="metric-median" />
        </div>
      </Section>

      {/* SYSTEM HEALTH — known counts vs not instrumented */}
      <Section icon={AlertTriangle} title="System health" testid="admin-attention">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A89A88]">Known current counts</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Metric label="Open support" value={attn.open_support ?? 0} />
          <Metric label="Abandoned Edit attempts" value={attn.abandoned_edits ?? 0} sub={`${attn.abandoned_edit_users ?? 0} unique users · attempts, not lost customers`}
            definition="Edit attempts recorded with an abandoned status. This is an ATTEMPT count; the unique-user count is shown separately. Money intentionally left unassigned is NOT counted here — see Product intelligence." testid="metric-abandoned" />
        </div>
        <p className="mt-4 mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A89A88]">Not instrumented</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Metric label="Failed notifications" value="Not tracked" />
          <Metric label="Failed exports" value="Not tracked" />
          <Metric label="Failed deletions" value="Not tracked" />
        </div>
        <p className="mt-3 text-[11px] leading-relaxed text-[#8A7C6B]">{ov?.system_health_note}</p>
      </Section>

      {/* PRODUCT INTELLIGENCE + CAPABILITY ADOPTION */}
      <Section icon={Activity} title="Product intelligence" testid="admin-product">
        <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Metric label="Unassigned adjustments" value={cap.unassigned_adjustments?.total ?? 0} sub={`${cap.unassigned_adjustments?.unique_users ?? 0} users`}
            definition="Adoption count of the append-only unassigned-money adjustment capability. Intentional unassigned behavior — not abandonment. No amounts shown." />
          <Metric label="Goal/Build corrections" value={cap.goal_build_corrections?.total ?? 0} sub={`${cap.goal_build_corrections?.unique_users ?? 0} users`}
            definition="Adoption count of append-only Goal/Build corrections. No amounts shown." />
          <Metric label="FI goals active" value={cap.fi_goals_active ?? 0} />
          <Metric label="FI assets reported" value={cap.fi_current_assets_reported ?? 0} />
        </div>
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A89A88]">Feature events (events vs unique users)</p>
        {product.length === 0 ? (
          <p className="text-sm text-[#8A7C6B]">Not enough activity in this period.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A89A88]">
                <th className="pb-1 font-semibold">Feature</th>
                <th className="pb-1 text-right font-semibold">Events</th>
                <th className="pb-1 pl-4 text-right font-semibold">Unique users</th>
              </tr>
            </thead>
            <tbody>
              {product.map((e) => (
                <tr key={e.name} className="border-t border-[#F0EBE2]">
                  <td className="py-1.5 text-[#171717]">{EVENT_LABELS[e.name] || e.name}</td>
                  <td className="py-1.5 text-right tabular-nums text-[#70584A]">{e.events}</td>
                  <td className="py-1.5 pl-4 text-right tabular-nums text-[#164C3B]">{e.unique_users}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="mt-3 text-[11px] text-[#8A7C6B]">Event totals count actions. Unique-user totals count people. No financial values are recorded.</p>
      </Section>
    </DashboardShell>
  );
}
